import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { Button, ContentLayout, Header, SpaceBetween, Table } from '@cloudscape-design/components';

import { deleteItem } from '../api/generic';
import DisplayImage from '../components/DisplayImage';
import ErrorBar from '../components/ErrorBar';
import { useAuthContext } from '../contexts/AuthContext';
import useSubscribe from '../hooks/useSubscribe';
import { Recipe } from '../models/Recipe';
import { prefixCollection } from '../utils/prefix';
import { sortName } from '../utils/sort';
import { transformData } from './Recipes.logic';
// import AiRecipe from "../preview/openai/AiRecipe";

const Recipes = () => {
  const { tenant } = useAuthContext();
  const navigate = useNavigate();
  const [recipes, setRecipes] = useState<Recipe[]>([]);

  const [viewModel, setViewModel] = useState<any[]>([]);
  const [error, setError] = useState<Error>();

  useSubscribe<Recipe>(prefixCollection('recipes', tenant), setRecipes, setError, sortName);

  useEffect(() => {
    setViewModel(transformData(recipes));
  }, [recipes]);

  const deleteRecipe = async (item: Recipe) => {
    const confirm = window.confirm(`Are you sure you want to delete ${item.name}?`);
    if (!confirm) return;

    await deleteItem(prefixCollection('recipes', tenant), item.id);
  };

  return (
    <ContentLayout
      disableOverlap
      header={
        <Header
          variant="h1"
          description={'Hint: Create ingredients before you create recipes'}
          actions={
            <SpaceBetween size="m" direction="horizontal">
              <Button iconName="add-plus" variant="primary" onClick={() => navigate('/recipes/new')}>
                Create recipe
              </Button>
              {/* <AiRecipe /> */}
            </SpaceBetween>
          }
        >
          Recipes
        </Header>
      }
    >
      <div style={{ marginTop: 10 }}>
        <ErrorBar error={error} setError={setError} />
        <Table
          wrapLines
          variant="embedded"
          items={viewModel}
          columnDefinitions={[
            {
              header: 'Image',
              cell: (item) => (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <DisplayImage
                    fileName="image"
                    filePath={'recipes/' + item.id}
                    round
                    altText="Recipe image"
                    style={{ height: 100, width: 100 }}
                  />
                  {item.experimental && (
                    <span
                      style={{
                        position: 'absolute',
                        top: 30,
                        right: 5,
                        backgroundColor: 'red',
                        color: 'white',
                        fontSize: 8,
                        fontWeight: 'bold',
                        padding: '2px 6px',
                        borderRadius: 4,
                        transform: 'translate(25%, -25%) rotate(30deg)',
                        transformOrigin: 'top right',
                        display: 'inline-block',
                      }}
                    >
                      Experimental
                    </span>
                  )}
                </div>
              ),
            },

            {
              header: 'Name',
              cell: (item) => (
                <Link to={`/recipes/${item.id}`}>
                  <strong>{item.name}</strong>
                </Link>
              ),
              isRowHeader: true,
            },
            {
              header: 'Style',
              cell: (item) => (
                <div
                  style={{
                    borderRadius: 4,
                    color: item.textColor,
                    backgroundColor: item.displayColor,
                    padding: 4,
                  }}
                >
                  {item.style}
                </div>
              ),
            },
            {
              header: 'Description',
              cell: (item) => <>{item.description}</>,
            },
            {
              header: 'Expected ABV',
              cell: (item) =>
                item?.defaultAbv !== undefined && !Number.isNaN(item.defaultAbv) ? `${item.defaultAbv}%` : '—',
            },
            {
              header: 'Ingredients',
              cell: (item) => <>{item.ingredients?.length ?? '—'}</>,
            },
            {
              header: 'Containers',
              cell: (item) => <>{item.containers?.length ?? '—'}</>,
            },
            {
              header: 'Steps',
              cell: (item) => <>{item.steps?.length ?? '—'}</>,
            },
            {
              header: 'Actions',
              cell: (item) => (
                <>
                  <Button iconName="remove" variant="inline-link" onClick={() => deleteRecipe(item)} />
                </>
              ),
            },
          ]}
        />
      </div>
    </ContentLayout>
  );
};

export default Recipes;
